<template>
  <div class="flex min-h-0 flex-col gap-3" data-test="authoring-assistant">
    <p class="flex-none text-gray-600">
      Tell the AI what to change in plain words. You decide whether to apply each change to your draft; live doesn't change until you publish.
    </p>
    <div v-if="!conversation.length" class="flex-none" data-test="authoring-examples">
      <div class="mb-1 text-[11px] font-medium uppercase tracking-wider text-gray-500">For example</div>
      <div class="flex flex-wrap gap-2">
        <button
          v-for="e in EXAMPLES" :key="e" type="button" :disabled="pending"
          class="rounded-full border border-gray-200 bg-white px-3 py-1 text-left text-gray-700 hover:border-gray-300 hover:bg-gray-50"
          @click="instruction = e"
        >{{ e }}</button>
      </div>
    </div>
    <ol v-else ref="turns" class="-mr-3 min-h-0 flex-1 space-y-2 overflow-y-auto pr-3" data-test="authoring-turns">
      <li v-for="r in conversation" :key="r.id" class="space-y-2">
        <div class="ml-auto w-fit max-w-[85%] rounded-lg bg-neutral-100 px-3 py-1.5 leading-snug text-gray-900">{{ r.instruction }}</div>
        <div :class="['w-fit max-w-[90%] rounded-lg border px-3 py-2 leading-snug', bubbleTone(r)]" :data-test="`authoring-reply-${r.id}`">
          <HermesWorking v-if="!isDone(r)" :label="r.status === 'queued' ? 'Waiting to start…' : 'Working on it… this can take up to a minute.'" :since="r.created_at" />
          <span v-else-if="r.status === 'failed'" class="text-gray-900">{{ r.error_message || 'The AI could not do this. Try asking another way.' }}</span>
          <span v-else-if="r.question" class="text-gray-900" data-test="authoring-question">{{ r.question }}</span>
          <template v-else>
            <p class="text-gray-900">{{ r.summary }}</p>
            <ul v-if="lines(r).length" class="mt-2 space-y-0.5" data-test="authoring-change-lines">
              <li v-for="(l, k) in lines(r)" :key="k" class="text-gray-700"><span :class="['mr-1 inline-block w-4 text-center font-semibold', l.tone]">{{ l.mark }}</span>{{ l.text }}</li>
            </ul>
            <p v-if="awaitingDecision(r) && r.draft_changed" class="mt-2.5 rounded border-l-2 border-primary-400 bg-gray-50 px-2.5 py-1.5 text-gray-700" data-test="authoring-draft-changed">
              The draft has changed since you asked. Applying this replaces those changes.
            </p>
            <div class="mt-2.5 flex flex-wrap items-center gap-3">
              <template v-if="awaitingDecision(r)">
                <button type="button" :class="PRIMARY_BUTTON" data-test="authoring-apply" @click="apply(r)">Apply to draft</button>
                <button type="button" :class="TEXT_BUTTON" data-test="authoring-dismiss" @click="record(r, 'rejected')">Dismiss</button>
              </template>
              <span v-else-if="r.outcome === 'accepted'" class="text-[11px] text-gray-500" data-test="authoring-applied">Applied to the draft</span>
              <span v-else-if="r.outcome === 'rejected'" class="text-[11px] text-gray-500">Dismissed</span>
              <button type="button" :class="TEXT_BUTTON" :data-test="`authoring-details-${r.id}`" @click="details = r">See details</button>
            </div>
          </template>
        </div>
      </li>
    </ol>
    <div v-if="error" role="alert" class="rounded-md bg-red-tint px-3 py-2 text-gray-900">{{ error }}</div>
    <div class="flex-none space-y-2">
      <textarea v-model="instruction" rows="3" :class="INPUT" :placeholder="awaitingAnswer ? 'Your answer…' : 'e.g. Remove the medication reconciliation summary, and keep the follow-up actions to three bullets.'" :disabled="pending" data-test="authoring-instruction" @keydown.meta.enter="ask('revise')" />
      <div class="flex items-center gap-3">
        <button v-if="conversation.length" type="button" :class="TEXT_BUTTON" :disabled="pending" data-test="authoring-new-conversation" @click="newConversation">New conversation</button>
        <span v-if="dirty && !pending" class="text-gray-500">Saving your changes first…</span>
        <button type="button" :class="[TEXT_BUTTON, 'ml-auto']" :disabled="blocked" data-test="derive-sections" @click="ask('derive_sections')">Tidy rules into sections</button>
        <button type="button" :class="PRIMARY_BUTTON" :disabled="blocked || !instruction.trim()" data-test="authoring-ask" @click="ask('revise')">{{ pending ? 'Working…' : (awaitingAnswer ? 'Reply' : 'Send') }}</button>
      </div>
    </div>

    <!-- Rendered at the end of <body>: inside the sticky chat panel it would sit under the document column. -->
    <Teleport to="body">
      <div v-if="details" class="fixed inset-0 z-40 flex items-center justify-center bg-gray-800/60 p-4" role="dialog" aria-modal="true" data-test="authoring-proposal" @click.self="details = null">
        <div class="flex max-h-[85vh] w-full max-w-[900px] flex-col rounded-lg border border-gray-200 bg-white">
          <div class="flex items-center rounded-t-lg bg-primary-100 px-4 py-3 font-semibold text-gray-900">
            What this change does
            <button type="button" class="ml-auto rounded px-2 text-xl font-normal leading-none text-gray-700 hover:bg-white/60" aria-label="Close" data-test="authoring-details-close" @click="details = null">×</button>
          </div>
          <div class="overflow-y-auto p-4 space-y-4">
            <p class="text-gray-900" data-test="authoring-summary">{{ details.summary }}</p>
            <HermesChangeSummary :before="details.base" :after="details" :title="title" before-label="Before" after-label="After" />
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import {
  createAuthoringRequest, getAuthoringRequest, listAuthoringRequests, recordAuthoringOutcome,
} from '@/services/api/hermesDrafts';
import HermesChangeSummary from './HermesChangeSummary.vue';
import HermesWorking from './HermesWorking.vue';
import { INPUT, PRIMARY_BUTTON, TEXT_BUTTON, errorOf } from './hermes';
import { documentChanges, promptChanges } from './templateChanges';

const POLL_MS = 1500;
const EXAMPLES = [
  'Remove a section that is usually empty',
  'Keep the summary to three sentences',
  'Add a section for medications the patient stopped',
  'Never guess dates; leave them blank',
];
const HISTORY_TURNS = 3;
const MARKS = {
  added: { mark: '+', tone: 'text-secondary-700' },
  removed: { mark: '−', tone: 'text-gray-500' },
  moved: { mark: '↕', tone: 'text-gray-700' },
  changed: { mark: '✎', tone: 'text-gray-700' },
};

export default {
  name: 'HermesAuthoringAssistant',
  components: { HermesChangeSummary, HermesWorking },
  props: {
    draftId: { type: Number, required: true },
    // The template's name, for the document preview in "See details".
    title: { type: String, default: 'Document' },
    // Edits not saved yet. The AI works on the saved draft, so asking waits for the autosave.
    dirty: { type: Boolean, default: false },
  },
  // apply: put a proposal into the draft (it is then saved).
  emits: ['apply'],
  data() {
    return { requests: [], since: null, instruction: '', error: '', timer: null, details: null, EXAMPLES, INPUT, PRIMARY_BUTTON, TEXT_BUTTON };
  },
  computed: {
    turns() {
      return [...this.requests].reverse();
    },
    // The current conversation: requests since "New conversation" (all of them until then), oldest first.
    conversation() {
      return this.since ? this.turns.filter((r) => r.created_at >= this.since) : this.turns;
    },
    awaitingAnswer() {
      const last = this.conversation[this.conversation.length - 1];
      return !!last && this.isDone(last) && !!last.question;
    },
    pending() {
      return this.requests.some((r) => !this.isDone(r));
    },
    blocked() {
      return this.dirty || this.pending;
    },
  },
  watch: {
    draftId: { immediate: true, handler: 'load' },
    // Keep the newest turn in view as the conversation grows or a reply comes in.
    requests() {
      this.$nextTick(() => { const el = this.$refs.turns; if (el) el.scrollTop = el.scrollHeight; });
    },
  },
  mounted() {
    window.addEventListener('keydown', this.onKey);
  },
  beforeUnmount() {
    clearTimeout(this.timer);
    window.removeEventListener('keydown', this.onKey);
  },
  methods: {
    onKey(event) {
      if (event.key === 'Escape' && this.details) this.details = null;
    },
    isDone(r) {
      return r.status === 'completed' || r.status === 'failed';
    },
    isProposal(r) {
      return r.status === 'completed' && !r.question && r.prompt !== null;
    },
    bubbleTone(r) {
      if (r.question && this.isDone(r)) return 'border-primary-300 bg-primary-50';
      return 'border-gray-200 bg-white';
    },
    // The change in plain lines: one per section, plus one for the general instructions.
    lines(r) {
      if (!this.isProposal(r)) return [];
      const doc = documentChanges(r.base, r).map((c) => ({ ...MARKS[c.kind], text: `${c.title}: ${c.kind}` }));
      const prompt = promptChanges(r.base.prompt, r.prompt);
      if (prompt.length) {
        doc.push({ ...MARKS.changed, text: `General instructions: ${prompt.length} ${prompt.length === 1 ? 'change' : 'changes'}` });
      }
      return doc;
    },
    // A finished proposal nobody has applied or dismissed yet.
    awaitingDecision(r) {
      return this.isProposal(r) && !r.outcome;
    },
    async load() {
      clearTimeout(this.timer);
      this.details = null;
      this.since = null;
      try {
        this.requests = await listAuthoringRequests(this.draftId);
      } catch (e) {
        this.requests = [];
        this.error = errorOf(e, 'Earlier requests could not be loaded.');
      }
      if (this.pending) this.poll();
    },
    history() {
      // The last few finished turns, oldest first, so a follow-up ("now shorten that one too") has context.
      return this.conversation.filter(this.isDone).slice(-HISTORY_TURNS).flatMap((r) => [
        { role: 'user', text: r.instruction.slice(0, 4000) },
        { role: 'assistant', text: (r.question || r.summary || r.error_message || '').slice(0, 4000) },
      ]).filter((t) => t.text);
    },
    newConversation() {
      this.since = new Date().toISOString();
      this.instruction = '';
    },
    async ask(mode) {
      if (this.blocked || (mode === 'revise' && !this.instruction.trim())) return;
      this.error = '';
      try {
        const created = await createAuthoringRequest(this.draftId, {
          mode, instruction: mode === 'revise' ? this.instruction.trim() : '', history: this.history(),
        });
        this.requests = [created, ...this.requests];
        if (mode === 'revise') this.instruction = '';
        this.poll();
      } catch (e) {
        this.error = errorOf(e, 'The request could not be sent.');
      }
    },
    async poll() {
      clearTimeout(this.timer);
      const open = this.requests.filter((r) => !this.isDone(r));
      try {
        const fresh = await Promise.all(open.map((r) => getAuthoringRequest(this.draftId, r.id)));
        this.requests = this.requests.map((r) => fresh.find((f) => f.id === r.id) || r);
      } catch (e) {
        this.error = errorOf(e, 'The request could not be loaded.');
        return;
      }
      if (this.pending) this.timer = setTimeout(() => this.poll(), POLL_MS);
    },
    apply(r) {
      this.$emit('apply', { prompt: r.prompt, result_schema: r.result_schema, display: r.display });
      this.record(r, 'accepted');
    },
    async record(r, outcome) {
      this.requests = this.requests.map((x) => (x.id === r.id ? { ...x, outcome } : x));
      try {
        await recordAuthoringOutcome(this.draftId, r.id, { outcome });
      } catch {
        // Recording the outcome is bookkeeping; the change itself has already been applied or dismissed.
      }
    },
  },
};
</script>
